import { createSlice } from '@reduxjs/toolkit';

// Dev-controlled feature flags, resolved per-org by the App Server (/feature-flags).
// `flags` is a { [code]: boolean } map; `developer` is a cosmetic indicator that gates
// the Developer → Feature Flags panel (writes are always enforced server-side).
const initialState = {
	flags: {},
	developer: false,
	loading: false,
	loaded: false,
	error: null,
	// Developer-panel registry (full flag list + all overrides). Loaded on demand;
	// writes go through developerCheck-gated admin endpoints.
	admin: {
		flags: [],
		overrides: [],
		loading: false,
		loaded: false,
		mutating: false,
		error: null,
		// All orgs (for the per-org override lookup — pick by name, not a raw ID). Loaded on
		// demand from /organisations/AllOrgs against whichever env this dashboard points at.
		orgs: [],
		orgsLoading: false,
		orgsLoaded: false
	}
};

const featureFlagsSlice = createSlice({
	name: 'featureFlags',
	initialState,
	reducers: {
		loadFeatureFlags: (state) => {
			state.loading = true;
			state.error = null;
		},
		loadFeatureFlagsSuccess: (state, action) => {
			state.loading = false;
			state.loaded = true;
			state.flags = action.payload.flags || {};
			state.developer = Boolean(action.payload.developer);
		},
		loadFeatureFlagsError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
		},

		// ── Developer admin (registry + writes) ──
		loadRegistry: (state) => {
			state.admin.loading = true;
			state.admin.error = null;
		},
		loadRegistrySuccess: (state, action) => {
			state.admin.loading = false;
			state.admin.loaded = true;
			state.admin.flags = action.payload.flags || [];
			state.admin.overrides = action.payload.overrides || [];
		},
		loadRegistryError: (state, action) => {
			state.admin.loading = false;
			state.admin.error = action.payload;
		},
		// ── Org lookup (for the override modal) ──
		loadOrgs: (state) => {
			state.admin.orgsLoading = true;
		},
		loadOrgsSuccess: (state, action) => {
			state.admin.orgsLoading = false;
			state.admin.orgsLoaded = true;
			state.admin.orgs = action.payload || [];
		},
		loadOrgsError: (state) => {
			state.admin.orgsLoading = false;
		},
		// Writes — the saga performs the request then re-loads the registry. The reducers
		// only toggle `mutating` (payloads are read by the saga).
		setPlatformDefault: (state) => {
			state.admin.mutating = true;
		},
		setOverride: (state) => {
			state.admin.mutating = true;
		},
		registerFlag: (state) => {
			state.admin.mutating = true;
		},
		updateFlagMeta: (state) => {
			state.admin.mutating = true;
		},
		archiveFlag: (state) => {
			state.admin.mutating = true;
		},
		unarchiveFlag: (state) => {
			state.admin.mutating = true;
		},
		mutateComplete: (state) => {
			state.admin.mutating = false;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState); // reset on logout
		// Reset on org-switch START so the OLD org's flags can't briefly resolve for the NEW
		// org during the refetch (cross-org bleed). While reloading, useFeature falls back —
		// module gates shown, new-feature gates hidden — until the new org's flags arrive.
		builder.addCase('auth/switchProfile', () => initialState);
	}
});

export const featureFlagActions = featureFlagsSlice.actions;
export default featureFlagsSlice.reducer;
